import React from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from 'admin/common/icons/CloseIcon';
import './ZoneIssueChangesModal.scss';
import { isRevertibleIssueChange } from './zoneIssueChangeUtils';

const RevertIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      d="M6.5 7.5L3.5 10.5L6.5 13.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 10.5H11.5C13.9853 10.5 16 12.5147 16 15V15.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CHANGE_BADGE_LABELS = {
  issued: 'Issued',
  returned: 'Returned',
  deleted: 'Deleted',
};

export default function ZoneIssueChangesModal({
  open,
  onClose,
  changes = [],
  onRevert,
  onRevertAll,
}) {
  const { t } = useTranslation();
  const hasRevertibleChanges = changes.some(isRevertibleIssueChange);

  if (!open) return null;

  return (
    <div
      className="zone-issue-changes-modal"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="zone-issue-changes-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="zone-issue-changes-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="zone-issue-changes-modal__header">
          <h2 id="zone-issue-changes-title" className="zone-issue-changes-modal__title">
            {t('Changes in this issue')}
          </h2>
          <div className="zone-issue-changes-modal__header-actions">
            {hasRevertibleChanges ? (
              <button
                type="button"
                className="zone-issue-changes-modal__revert-all"
                onClick={() => onRevertAll?.()}
                aria-label={t('Revert all')}
                title={t('Revert all')}
              >
                <RevertIcon />
              </button>
            ) : null}
            <button
              type="button"
              className="zone-issue-changes-modal__close"
              onClick={onClose}
              aria-label={t('Close')}
            >
              <CloseIcon />
            </button>
          </div>
        </header>

        <div className="zone-issue-changes-modal__body">
          {changes.length ? (
            <ul className="zone-issue-changes-modal__list">
              {changes.map((change) => {
                const types = change.types || [];
                const primaryType = types[0] || 'issued';
                const showRevert = isRevertibleIssueChange(change);

                return (
                <li
                  key={change.key}
                  className={`zone-issue-changes-modal__item${
                    change.isNewRow
                      ? ' zone-issue-changes-modal__item--new-row'
                      : ''
                  }`}
                >
                  <div className="zone-issue-changes-modal__item-meta">
                    <span className="zone-issue-changes-modal__sku">
                      {change.SKU}
                    </span>
                    <span className="zone-issue-changes-modal__stock-id">
                      {change.stockId}
                    </span>
                  </div>
                  <div className="zone-issue-changes-modal__item-action">
                    <div className="zone-issue-changes-modal__badges">
                      {types.map((type) => (
                        <span
                          key={`${change.key}-${type}`}
                          className={`zone-issue-changes-modal__badge zone-issue-changes-modal__badge--${type}`}
                        >
                          {t(CHANGE_BADGE_LABELS[type] || type)}
                        </span>
                      ))}
                    </div>
                    {showRevert ? (
                      <button
                        type="button"
                        className="zone-issue-changes-modal__revert-btn"
                        onClick={() =>
                          onRevert?.({
                            ...change,
                            type: primaryType,
                          })
                        }
                        aria-label={t('Revert change')}
                        title={t('Revert change')}
                      >
                        <RevertIcon />
                      </button>
                    ) : null}
                  </div>
                </li>
                );
              })}
            </ul>
          ) : (
            <p className="zone-issue-changes-modal__empty">
              {t('No unsaved changes in this issue.')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
